import { useEffect, useMemo, useState } from 'react';
import Button from '@/components/base/Button';
import Modal from '@/components/base/Modal';
import StatusBadge from '@/components/base/StatusBadge';
import { formatMoney } from '@/utils/format';
import type { fundingMethods, mobileMoneyProviders, walletBankAccounts } from '@/mocks/wallets';
import type { Wallet } from '@/types/portal';
import TopUpBankDetails from './TopUpBankDetails';
import TopUpMobileMoney from './TopUpMobileMoney';
import TopUpCardForm from './TopUpCardForm';
import { cardLast4, emptyCard, isCardValid, type CardFormState } from './cardForm';

type Step = 'wallet' | 'amount' | 'method';
export type MethodId = 'bank' | 'mobile' | 'card';
type TopUpStatus = 'Processing' | 'Completed';

/** How a wallet can be funded (GET /funding/methods and /funding/bank-accounts). */
export interface FundingOptions {
  methods: typeof fundingMethods;
  mobileMoneyProviders: typeof mobileMoneyProviders;
  bankAccounts: typeof walletBankAccounts;
}

interface TopUpModalProps {
  open: boolean;
  wallets: Wallet[];
  preselect: Wallet | null;
  onClose: () => void;
  funding: FundingOptions;
  onConfirm: (wallet: Wallet, amount: number, methodLabel: string, status: TopUpStatus, method: MethodId) => void;
  done: boolean;
  reference: string;
}

const STEPS: { key: Step; label: string }[] = [
  { key: 'wallet', label: 'Wallet' },
  { key: 'amount', label: 'Amount' },
  { key: 'method', label: 'Method' },
];

const presetsFor = (currency: string): number[] => {
  if (currency === 'TZS') return [2000000, 5000000, 10000000, 20000000];
  if (currency === 'UGX') return [1000000, 2500000, 5000000, 10000000];
  if (currency === 'USD') return [500, 1000, 2500, 5000];
  return [100000, 250000, 500000, 1000000];
};

export default function TopUpModal({
  open,
  wallets,
  preselect,
  funding,
  onClose,
  onConfirm,
  done,
  reference,
}: TopUpModalProps) {
  const { methods: fundingMethods, mobileMoneyProviders, bankAccounts: walletBankAccounts } = funding;
  const [step, setStep] = useState<Step>('wallet');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<MethodId | null>(null);
  const [providerId, setProviderId] = useState(mobileMoneyProviders[0].id);
  const [phone, setPhone] = useState('');
  const [card, setCard] = useState<CardFormState>(emptyCard);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep('wallet');
    setSelectedId(preselect?.id ?? null);
    setAmount('');
    setMethod(null);
    setProviderId(mobileMoneyProviders[0].id);
    setPhone('');
    setCard(emptyCard);
    setProcessing(false);
  }, [open, preselect, mobileMoneyProviders]);

  const wallet = useMemo(
    () => wallets.find((item) => item.id === selectedId) ?? null,
    [wallets, selectedId],
  );

  const bankAccount = wallet
    ? walletBankAccounts[wallet.currency] ?? walletBankAccounts.USD
    : walletBankAccounts.USD;

  const numeric = Number(amount.replace(/[^0-9]/g, '')) || 0;
  const amountValid = numeric > 0;
  const validCard = isCardValid(card);
  const provider = mobileMoneyProviders.find((item) => item.id === providerId) ?? mobileMoneyProviders[0];

  const methodLabel = (() => {
    if (method === 'bank') return `Bank transfer · ${bankAccount.bankName}`;
    if (method === 'mobile') return `${provider.label} collection`;
    if (method === 'card') return `Card · •••• ${cardLast4(card)}`;
    return '';
  })();

  const chooseWallet = (next: Wallet) => {
    setSelectedId(next.id);
    setAmount('');
    setMethod(null);
  };

  const patchCard = (patch: Partial<CardFormState>) => setCard((current) => ({ ...current, ...patch }));

  const confirmBank = () => {
    if (!wallet) return;
    onConfirm(wallet, numeric, methodLabel, 'Processing', 'bank');
  };

  const confirmCard = () => {
    if (!wallet || !validCard) return;
    setProcessing(true);
    window.setTimeout(() => {
      setProcessing(false);
      onConfirm(wallet, numeric, methodLabel, 'Completed', 'card');
    }, 1200);
  };

  const sendPush = () => {
    if (!wallet) return;
    setProcessing(true);
    window.setTimeout(() => {
      setProcessing(false);
      onConfirm(wallet, numeric, methodLabel, 'Processing', 'mobile');
    }, 1600);
  };

  const stepIndex = STEPS.findIndex((item) => item.key === step);

  const doneTitle =
    method === 'bank' ? 'Transfer instructions ready' : method === 'card' ? 'Payment received' : 'Prompt sent';

  const doneDetail = (() => {
    if (!wallet) return '';
    if (method === 'bank')
      return `Send ${formatMoney(numeric, wallet.currency)} to ${bankAccount.bankName} · ${bankAccount.accountNumber}. The wallet credits once the transfer clears.`;
    if (method === 'mobile')
      return `A collection prompt was pushed to ${phone || 'your phone'}. Approve it there to credit the wallet.`;
    if (method === 'card')
      return `Paid with card ending ${cardLast4(card)}. Funds are available in the wallet immediately.`;
    return '';
  })();

  const footer = done ? (
    <Button onClick={onClose}>Done</Button>
  ) : step === 'wallet' ? (
    <>
      <Button variant="outline" onClick={onClose}>
        Cancel
      </Button>
      <Button
        icon="ri-arrow-right-line"
        disabled={!wallet}
        onClick={() => setStep('amount')}
      >
        Continue
      </Button>
    </>
  ) : step === 'amount' ? (
    <>
      <Button variant="outline" icon="ri-arrow-left-line" onClick={() => setStep('wallet')}>
        Back
      </Button>
      <Button
        icon="ri-arrow-right-line"
        disabled={!amountValid}
        onClick={() => setStep('method')}
      >
        Continue
      </Button>
    </>
  ) : (
    <>
      <Button variant="outline" icon="ri-arrow-left-line" onClick={() => setStep('amount')}>
        Back
      </Button>
      {method === 'bank' ? (
        <Button icon="ri-check-double-line" onClick={confirmBank}>
          I&apos;ve made the transfer
        </Button>
      ) : method === 'mobile' ? (
        <Button
          icon="ri-notification-3-line"
          disabled={phone.trim().length < 6 || processing}
          onClick={sendPush}
        >
          {processing ? 'Sending push…' : 'Send push notification'}
        </Button>
      ) : method === 'card' ? (
        <Button icon="ri-lock-2-line" disabled={!validCard || processing} onClick={confirmCard}>
          {processing ? 'Processing…' : `Pay ${wallet ? formatMoney(numeric, wallet.currency) : ''}`}
        </Button>
      ) : (
        <Button disabled>Choose a method</Button>
      )}
    </>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      icon={done ? 'ri-checkbox-circle-line' : wallet ? 'ri-safe-2-line' : 'ri-add-circle-line'}
      title={done ? doneTitle : 'Fund wallet'}
      subtitle={
        done
          ? reference
          : wallet
            ? `${wallet.ownerName} · ${wallet.currency}`
            : 'Choose the wallet you want to fund'
      }
      widthClassName="max-w-lg"
      footer={footer}
    >
      {done ? (
        <div className="flex flex-col items-center py-2 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-accent-100 text-2xl text-accent-700">
            <i className="ri-check-line" />
          </span>
          <p className="mt-3 max-w-sm text-sm text-foreground-700">{doneDetail}</p>
          {wallet ? (
            <div className="mt-4 w-full rounded-md border border-background-200 bg-background-100 px-4 py-3 text-left">
              <div className="flex items-center justify-between text-sm">
                <span className="text-foreground-500">Amount</span>
                <span className="text-foreground-800">
                  {formatMoney(numeric, wallet.currency)}
                </span>
              </div>
              <div className="mt-1 flex items-center justify-between text-sm">
                <span className="text-foreground-500">New available balance</span>
                <span className="font-medium text-foreground-950">
                  {formatMoney(wallet.balance + numeric, wallet.currency)}
                </span>
              </div>
              <p className="mt-1.5 text-[11px] text-foreground-500">
                {methodLabel} · reference {reference}
              </p>
            </div>
          ) : null}
        </div>
      ) : (
        <div className="space-y-5">
          <ol className="flex items-center gap-2">
            {STEPS.map((item, index) => {
              const active = index === stepIndex;
              const complete = index < stepIndex;
              return (
                <li key={item.key} className="flex flex-1 items-center gap-2">
                  <span
                    className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-label font-semibold ${
                      complete
                        ? 'bg-primary-500 text-background-50'
                        : active
                          ? 'bg-primary-100 text-primary-700'
                          : 'bg-background-200 text-foreground-500'
                    }`}
                  >
                    {complete ? <i className="ri-check-line" /> : index + 1}
                  </span>
                  <span
                    className={`text-xs font-label ${active || complete ? 'text-foreground-900' : 'text-foreground-400'}`}
                  >
                    {item.label}
                  </span>
                  {index < STEPS.length - 1 ? (
                    <span
                      className={`h-px flex-1 ${complete ? 'bg-primary-300' : 'bg-background-200'}`}
                    />
                  ) : null}
                </li>
              );
            })}
          </ol>

          {step === 'wallet' ? (
            <div className="space-y-2">
              {wallets.map((option) => {
                const active = option.id === selectedId;
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => chooseWallet(option)}
                    className={`flex w-full items-center justify-between gap-3 rounded-md border px-3 py-2.5 text-left transition-colors cursor-pointer ${
                      active
                        ? 'border-primary-400 bg-primary-50'
                        : 'border-background-300 bg-background-50 hover:bg-background-100'
                    }`}
                  >
                    <span className="flex min-w-0 items-center gap-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-[11px] font-semibold text-secondary-800">
                        {option.currency}
                      </span>
                      <span className="min-w-0">
                        <span className="block truncate text-sm text-foreground-900">
                          {option.ownerName}
                        </span>
                        <span className="block text-[11px] text-foreground-500">
                          {option.market} · {formatMoney(option.balance, option.currency)}
                        </span>
                      </span>
                    </span>
                    <span className="flex shrink-0 items-center gap-2">
                      <StatusBadge status={option.status} />
                      <i
                        className={
                          active
                            ? 'ri-checkbox-circle-fill text-primary-500'
                            : 'ri-circle-line text-foreground-300'
                        }
                      />
                    </span>
                  </button>
                );
              })}
            </div>
          ) : null}

          {step === 'amount' && wallet ? (
            <div>
              <label
                htmlFor="topup-amount"
                className="mb-1.5 block text-xs font-label font-medium text-foreground-700"
              >
                Amount in {wallet.currency}
              </label>
              <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3">
                <span className="text-sm font-medium text-foreground-500">{wallet.currency}</span>
                <input
                  id="topup-amount"
                  type="text"
                  inputMode="numeric"
                  autoFocus
                  value={amount}
                  onChange={(event) => setAmount(event.target.value)}
                  placeholder="0"
                  className="h-10 w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
                />
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {presetsFor(wallet.currency).map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => setAmount(String(preset))}
                    className="rounded-full border border-background-300 bg-background-50 px-3 py-1 text-xs font-label text-foreground-700 transition-colors cursor-pointer hover:border-primary-300 hover:bg-primary-50"
                  >
                    +{formatMoney(preset, wallet.currency)}
                  </button>
                ))}
              </div>

              <div className="mt-4 rounded-md border border-background-200 bg-background-100 px-4 py-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-foreground-500">Current balance</span>
                  <span className="text-foreground-800">
                    {formatMoney(wallet.balance, wallet.currency)}
                  </span>
                </div>
                <div className="mt-1 flex items-center justify-between text-sm">
                  <span className="text-foreground-500">After top-up</span>
                  <span className="font-medium text-foreground-950">
                    {formatMoney(wallet.balance + numeric, wallet.currency)}
                  </span>
                </div>
              </div>
            </div>
          ) : null}

          {step === 'method' && wallet ? (
            <div className="space-y-4">
              <div className="space-y-2">
                {fundingMethods.map((option) => {
                  const active = option.id === method;
                  return (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => setMethod(option.id as MethodId)}
                      className={`flex w-full items-center justify-between gap-3 rounded-md border px-3 py-2.5 text-left transition-colors cursor-pointer ${
                        active
                          ? 'border-primary-400 bg-primary-50'
                          : 'border-background-300 bg-background-50 hover:bg-background-100'
                      }`}
                    >
                      <span className="flex min-w-0 items-center gap-2.5">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-background-100 text-base text-foreground-600">
                          <i className={option.icon} />
                        </span>
                        <span className="min-w-0">
                          <span className="block text-sm text-foreground-900">{option.label}</span>
                          <span className="block text-[11px] text-foreground-500">
                            {option.hint}
                          </span>
                        </span>
                      </span>
                      <i
                        className={
                          active
                            ? 'ri-checkbox-circle-fill text-primary-500'
                            : 'ri-circle-line text-foreground-300'
                        }
                      />
                    </button>
                  );
                })}
              </div>

              {method === 'bank' ? (
                <TopUpBankDetails
                  bankName={bankAccount.bankName}
                  accountName={bankAccount.accountName}
                  accountNumber={bankAccount.accountNumber}
                  swiftCode={bankAccount.swiftCode}
                  currency={wallet.currency}
                  amount={numeric}
                />
              ) : null}

              {method === 'mobile' ? (
                <TopUpMobileMoney
                  providers={mobileMoneyProviders}
                  providerId={providerId}
                  onProvider={setProviderId}
                  phone={phone}
                  onPhone={setPhone}
                  currency={wallet.currency}
                  amount={numeric}
                />
              ) : null}

              {method === 'card' ? (
                <TopUpCardForm
                  card={card}
                  onChange={patchCard}
                  currency={wallet.currency}
                  amount={numeric}
                />
              ) : null}
            </div>
          ) : null}
        </div>
      )}
    </Modal>
  );
}